'use client';

import { getIdentity } from '@hyphengroup/cms-components/modules/auth';
import { useEffect, useState } from 'react';
import Log from 'utils/logging.client';
import type { ApplyMode } from '$/(cms)/_modules/routing';
import type { IdentityClaims } from '$/modules/auth/shared';

/**
 * Which lead-capture CTA variant the user gets. `pending` exists because the
 * identity lives in a cookie the statically cached page cannot read.
 */
export type ApplyLoginStatus =
  | { state: 'pending' }
  | { state: 'anonymous' }
  | { state: 'identified'; email: string };

export const LOGIN_STATUS_CHECK_FAILED_LOG =
  'lead_capture_login_status_check_failed';

export const IDENTITY_SWITCH_LOGOUT_FAILED_LOG =
  'lead_capture_identity_switch_logout_failed';

const PENDING: ApplyLoginStatus = { state: 'pending' };
const ANONYMOUS: ApplyLoginStatus = { state: 'anonymous' };

export function resolveLoginStatus(
  identity: IdentityClaims | undefined,
  nowMs: number,
): ApplyLoginStatus {
  const email = identity?.email?.trim();
  if (!email) return ANONYMOUS;
  // A token with no `exp` claim is treated as live: an unreadable expiry must
  // not lock out a user the cookie says is signed in.
  if (typeof identity?.exp === 'number' && identity.exp * 1000 <= nowMs) {
    return ANONYMOUS;
  }
  return { state: 'identified', email };
}

/** Reads the current login status, falling back to the anonymous variant. */
export function readLoginStatus(mode: ApplyMode): ApplyLoginStatus {
  try {
    return resolveLoginStatus(getIdentity(), Date.now());
  } catch (error) {
    Log.error({ message: LOGIN_STATUS_CHECK_FAILED_LOG, mode, error });
    return ANONYMOUS;
  }
}

export function useApplyLoginStatus(
  mode: ApplyMode,
): [ApplyLoginStatus, (status: ApplyLoginStatus) => void] {
  const [status, setStatus] = useState<ApplyLoginStatus>(PENDING);
  useEffect(() => setStatus(readLoginStatus(mode)), [mode]);
  return [status, setStatus];
}
